/**
 * GroupBySection — basic GROUP BY clause rendering. Each comma-separated
 * column is a row; in RO mode each row ships disabled `×` and the section
 * ships disabled `[+ add group-by]`.
 *
 * VQE-Edit wiring: when `editContext` is supplied AND the resolver service
 * has a schema snapshot, the per-row `×` button commits a `removeGroupBy` op
 * and the `[+ add group-by]` popover is populated by `useColumnSuggestions`
 * (committing an `addGroupBy`). Without `editContext` (or without a snapshot)
 * the disabled VQE-RO scaffolding renders unchanged.
 *
 * The aggregate-awareness warning ([+ auto-add to GROUP BY] hint) lives on
 * `SelectList` rather than here, since the hint targets non-aggregate SELECT
 * items in a mixed query.
 *
 * Drag-reorder (Plan 8 T4, §2.7 S12): chips reorder via the `reorder-group-by`
 * op — the same `dnd-kit` idiom SELECT chips use (`SelectList.tsx`). Chips are
 * only draggable when `computeGroupBySortable` (decision 5) holds: edit is
 * ready AND the UI's top-level-comma split agrees with `structure.groupBy`'s
 * item count. The resolver planner double-guards the same count and bails to
 * a no-op splice on disagreement — this UI gate is the first line of defense.
 */

import { useMemo } from 'react';

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, sortableKeyboardCoordinates } from '@dnd-kit/sortable';

import type { ParseOptions } from '../../../../sql-grammar/model';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext, toCommitCtx } from '../../edit/editContext';
import { removeGroupBy } from '../../edit/edits/removeGroupBy';
import { reorderGroupBy } from '../../edit/edits/reorderGroupBy';
import { inScopeRelationPrefixes, shouldFlagOutOfScope } from '../common/inScopeRelations';
import { ClauseCard, EMPTY_CLAUSE_LABEL } from './ClauseCard';
import type { SelectOutline } from './extractSelectOutline';
import { GroupByAddPopover } from './GroupByAddPopover';
import { GroupByChip } from './GroupByChip';
import { splitTopLevelCommas } from './splitTopLevel';

export interface GroupBySectionProps {
  readonly bufferText: string;
  readonly outline: SelectOutline;
  readonly options: ParseOptions;
  readonly editContext?: VqeEditContext;
  /** Render nothing for an empty clause — the SELECT's clause add row offers it. */
  readonly hideEmpty?: boolean;
}

/**
 * Decision 5's gating formula, extracted as a pure function for direct unit
 * coverage: chips are sortable only once edit is ready AND the UI's item
 * count agrees with `structure.groupBy`'s item count (`null` when the
 * structure could not be resolved). A disagreement means the UI split and the
 * resolver's extraction parsed an expression-shaped GROUP BY item
 * differently — rendering an undraggable chip is safer than committing a
 * misaddressed op.
 */
export function computeGroupBySortable(
  editReady: boolean,
  uiItemCount: number,
  structureGroupByCount: number | null,
): boolean {
  return editReady && structureGroupByCount !== null && uiItemCount === structureGroupByCount;
}

/**
 * Resolve a dnd-kit drag-end event's active/over ids into 0-based indices, or
 * `null` when the drag should NOT commit — no drop target (`over == null`),
 * dropped back on itself, or a non-integer id. `GroupBySection`'s
 * `handleDragEnd` returns immediately on `null`, before calling
 * `reorderGroupBy` or `track()`.
 */
export function resolveGroupByDragIndices(
  activeId: string,
  overId: string | null,
): { fromIndex: number; toIndex: number } | null {
  if (overId == null || activeId === overId) return null;
  const fromIndex = Number(activeId);
  const toIndex = Number(overId);
  if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)) return null;
  return { fromIndex, toIndex };
}

export function GroupBySection({ bufferText, outline, options, editContext, hideEmpty }: GroupBySectionProps) {
  const editReady = !!editContext && !!editContext.service.getSchemaSnapshot();

  // Folded aliases + table names in the query's FROM/JOIN — drives the
  // out-of-scope red marking on entries referencing an absent table.
  const inScopePrefixes = useMemo(() => {
    if (!editContext || !editContext.service.getSchemaSnapshot()) return new Set<string>();
    try {
      return inScopeRelationPrefixes(extractStructureForContext(editContext));
    } catch {
      return new Set<string>();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContext, bufferText]);

  // `structure.groupBy`'s item count, resolved the same way `inScopePrefixes`
  // resolves the structure — `null` when unavailable (no snapshot yet, or
  // extraction failed) so `computeGroupBySortable` treats it as "not ready".
  const structureGroupByCount = useMemo(() => {
    if (!editContext || !editContext.service.getSchemaSnapshot()) return null;
    try {
      return extractStructureForContext(editContext).groupBy?.length ?? 0;
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editContext, bufferText]);

  const { track } = useAnalytics();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const items = outline.groupBy
    ? splitTopLevelCommas(bufferText, outline.groupBy.start, outline.groupBy.end, options)
    : [];

  const sortable = computeGroupBySortable(editReady, items.length, structureGroupByCount);

  const handleDragEnd = (event: DragEndEvent) => {
    if (!editContext) return;
    const move = resolveGroupByDragIndices(
      String(event.active.id),
      event.over ? String(event.over.id) : null,
    );
    if (!move) return;
    void reorderGroupBy(move.fromIndex, move.toIndex, toCommitCtx(editContext));
    track('feature:vqe_reorder_columns', { target: 'group_by' });
  };

  // When the section is missing entirely AND we can edit, we still render the
  // [+ add group-by] affordance so the user can synthesize GROUP BY from
  // scratch. The VQE-RO null-return is preserved for the no-editContext path.
  if (!outline.groupBy && !editContext) return null;

  // Empty + editable → a one-line keyword row (`GROUP BY — none · [+ Group by
  // column]`) instead of a full card, so an unused clause costs one line and the
  // action reads as belonging to GROUP BY (not to WHERE above it). The RO empty
  // diagnostic card is preserved below for the no-edit path.
  if (items.length === 0 && editContext) {
    if (hideEmpty) return null;
    return (
      <ClauseCard
        title="Group By"
        testId="vqe-group-by-row"
        density="compact"
        footer={<GroupByAddPopover editContext={editContext} />}
      >
        <span className="text-xs italic text-muted-foreground">{EMPTY_CLAUSE_LABEL}</span>
      </ClauseCard>
    );
  }

  const chipNodes = items.map((item, idx) => (
    <GroupByChip
      key={`${item.text}-${idx}`}
      id={String(idx)}
      index={idx}
      text={item.text}
      outOfScope={shouldFlagOutOfScope(item.text, inScopePrefixes)}
      sortable={sortable}
      removable={editReady}
      onRemove={
        editReady && editContext
          ? () => {
              void removeGroupBy(idx, toCommitCtx(editContext));
            }
          : undefined
      }
    />
  ));

  return (
    <ClauseCard
      title="Group By"
      testId="vqe-group-by-section"
      density="line"
      footer={<GroupByAddPopover editContext={editContext} />}
    >
      {items.length === 0 ? (
        <p className="text-xs italic text-muted-foreground" data-testid="vqe-group-by-empty">
          No columns parsed.
        </p>
      ) : sortable ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((_, idx) => String(idx))} strategy={rectSortingStrategy}>
            <ul className="flex flex-wrap gap-1.5" data-testid="vqe-group-by-items">
              {chipNodes}
            </ul>
          </SortableContext>
        </DndContext>
      ) : (
        <ul className="flex flex-wrap gap-1.5" data-testid="vqe-group-by-items">
          {chipNodes}
        </ul>
      )}
    </ClauseCard>
  );
}
